import { getUsername } from "@amzn/midway-cognito-type-script-provider";

import type { FeatureFlagsState } from "../store/featureFlags";
import { FeatureFlagsStore, getFlagValues } from "../store/featureFlags";
import type { UserPreferenceList } from "../store/userPreferences";
import {
    initialState,
    UserPreference,
    UserPreferenceData,
    UserPreferencesStore,
} from "../store/userPreferences";

import LocalStorageManager from "./localStorage.utils";

// Static key for where we will store user preferences in local storage
const USER_PREF_LOCAL_STORAGE_KEY = "UserPreferences";

/**
 * Retrieves the saved user preferences from storage
 * TODO: This will be migrated to an API call later, so we are marking it as async for now
 * @returns The user's current preferences
 */
export const loadUserPreferences = async (): Promise<UserPreferenceList> => {
    const savedPrefs =
        (LocalStorageManager.getWithExpiry(
            USER_PREF_LOCAL_STORAGE_KEY,
        ) as UserPreferenceList) ?? {};

    // Fill in any blanks we may have from newly added features since the user last saved
    Object.keys(UserPreference).forEach((preference) => {
        const key = preference as UserPreference;
        const preferenceValue = savedPrefs[key];
        const preferenceData = UserPreferenceData[preference];

        // If there is no value, and we have a feature flag, grab the value from our feature flag store
        if (
            preferenceValue === undefined &&
            preferenceData.featureFlagControl !== undefined
        ) {
            // The featureFlagControl contains the enum value, use it directly as the key
            const featureFlagValue =
                FeatureFlagsStore.getState()[
                    preferenceData.featureFlagControl as keyof FeatureFlagsState
                ];
            if (featureFlagValue !== undefined) {
                savedPrefs[key] = featureFlagValue.toString();
            } else {
                savedPrefs[key] = initialState[key];
            }
            // else, if we still have no value, set it to the initial state
        } else if (preferenceValue === undefined) {
            savedPrefs[key] = initialState[key];
        }
    });

    return savedPrefs;
};

/**
 * Saves the users current preferences to storage
 */
export const saveUserPreferences = async (): Promise<void> => {
    const preferences = UserPreferencesStore.getState().getAllPreferences();
    LocalStorageManager.storeWithExpiry(
        USER_PREF_LOCAL_STORAGE_KEY,
        preferences,
        365,
    );

    // Refresh the feature flag store
    const flags = await getFlagValues(await getUsername());
    FeatureFlagsStore.getState().setFeatureFlags(flags);
};
